import { useState } from "react";
import { Link } from "react-router-dom";
import { BASE_URL } from '../apiConfig';
import { useAuth } from "../context/AuthProvider";
import DeleteModal from '../cors/DeleteModal'; 
import { ShieldCheck, UserRoundCog } from 'lucide-react'
import { toast } from 'sonner';

import { useLanguage } from "../context/LanguageProvider";

export default function AdminCard({ admin , fetchUsers}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { token } = useAuth();
  const { t } = useLanguage();
  
  const deleteAdmin = async () => {
    setIsModalOpen(false); 

    try {
      const res = await fetch(BASE_URL+`/api/users/${admin.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      if (res.ok) {
        toast.success("Suppression réussie");
        fetchUsers();
      } else {
        const err = await res.json()
        toast.error("Echec de suppression");
      }
    } catch (error) {
      toast.error("Echec de suppression");
    }
  }

  const formatPhone = (phone) => {
    if (!phone) return t('admins.not_provided');
    const phoneStr = phone.toString();
    return phoneStr.replace(/(\d{2})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const getActivityStatus = () => {
    if (!admin.info.last_login_at || admin.info.last_login_at === "") {
      return { 
        status: t('admins.never_connected'), 
        color: "text-red-600 bg-red-100",
        bg: "bg-red-600",
        tooltip: t('admins.tooltip_never_connected')
      };
    }
    
    const lastLogin = new Date(admin.info.last_login_at);
    const now = new Date();
    const diffInSeconds = Math.floor((now - lastLogin) / 1000);
    const minutesPassed = Math.floor(diffInSeconds / 60);
    const hoursPassed = Math.floor(minutesPassed / 60);
    const daysPassed = Math.floor(hoursPassed / 24);

    if (diffInSeconds < 60) {
      return {
        status: t('admins.online_now'),
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: `${t('admins.tooltip_online_now')} (${lastLogin.toLocaleString()})`
      };
    }
    if (minutesPassed < 60) {
      return {
        status: t('admins.online_min', { minutes: minutesPassed }),
        color: "text-green-600 bg-green-100",
        bg: "bg-green-600",
        tooltip: t('admins.tooltip_min_ago', { minutes: minutesPassed })
      };
    }
    if (hoursPassed < 24) {
      return {
        status: t('admins.online_hours', { hours: hoursPassed }),
        color: "text-blue-600 bg-blue-100",
        bg: "bg-blue-600",
        tooltip: t('admins.tooltip_hours_ago', { hours: hoursPassed })
      };
    }
    if (daysPassed === 1) {
      return {
        status: t('admins.online_day'),
        color: "text-yellow-600 bg-yellow-100",
        bg: "bg-yellow-600",
        tooltip: `${t('admins.tooltip_yesterday')} (${lastLogin.toLocaleString()})`
      };
    }
    if (daysPassed < 7) {
      return {
        status: t('admins.online_days', { days: daysPassed }),
        color: "text-amber-600 bg-amber-100",
        bg: "bg-amber-600",
        tooltip: t('admins.tooltip_days_ago', { days: daysPassed })
      };
    }
    if (daysPassed < 30) {
      const weeksPassed = Math.floor(daysPassed / 7);
      return {
        status: t('admins.online_weeks', { weeks: weeksPassed }),
        color: "text-orange-600 bg-orange-100",
        bg: "bg-orange-600",
        tooltip: t('admins.tooltip_weeks_ago', { weeks: weeksPassed })
      };
    }
    return {
      status: t('admins.inactive'),
      color: "text-red-600 bg-red-100",
      bg: "bg-red-600",
      tooltip: `${t('admins.tooltip_last_activity_month')} (${lastLogin.toLocaleDateString()})`
    };
  };

  const activity = getActivityStatus();
  const userType = admin.type === 1 ? t('admins.user_type_admin') : t('admins.user_type_assistant');

  return (
    <div className="relative group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:-translate-y-2">
      {/* Header */}
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-2xl flex items-center justify-center">
             {
              userType === "Admin" ?
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-2xl flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-[var(--color-primary-500)]" />
              </div>
              :
              <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                <UserRoundCog  className="h-6 w-6 text-orange-600" />
              </div>
             }
          </div>
          <div className="flex flex-col items-end">
            <span className={`px-3 py-1 rounded-full font-semibold text-[11px] ${activity.color} mb-1`}>
              {activity.status}
            </span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 group-hover:text-[var(--color-primary-500)] transition-colors duration-300">
          {admin.info?.name || admin.email}
        </h3>
        {userType === "Admin" ? 
          <span className="text-[11px] bg-green-200 rounded-full pt-0.2 pb-0.5 px-2 text-green-600">{userType}</span> 
          :
          <span className="text-[11px] bg-orange-200 rounded-full pt-0.2 pb-0.5 px-2 text-orange-600">{userType}</span>
        }
        <p className="text-gray-600 text-xs mt-2">{admin.email}</p>
      </div>

      <div className="px-4 pb-4">
        <div className="space-y-3 mb-6">
          <div className="flex items-center text-xs text-gray-600">
            <svg className="h-3.5 w-3.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            {admin.info?.gender || t('admins.not_specified')}
          </div>
          <div className="flex items-center text-xs text-gray-600">
            <svg className="h-3.5 w-3.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            {admin.info?.phone ? formatPhone(admin.info?.phone) : t('admins.not_provided')}
          </div>
          
          {admin.type === 2 && admin.roles && admin.roles.length > 0 && (
            <div className="flex items-start text-xs text-gray-600">
              <svg className="h-3.5 w-3.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <div>
                <p className="font-medium">{t('admins.role_label')}: {admin.roles[0].name}</p>
              </div>
            </div>
          )}
        </div>

        <div className="absolute bottom-3 right-5 flex gap-2">
          <Link to={`/admins/${admin.id}`}>
            <button className="p-2 border-2 border-gray-200 cursor-pointer hover:border-[var(--color-primary-500)] text-gray-600 hover:text-[var(--color-primary-500)] rounded-xl transition-all duration-300">
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
          </Link>
          <button 
            onClick={() => setIsModalOpen(true)} 
            className="p-2 border-2 border-gray-200 cursor-pointer hover:border-red-400 text-gray-600 hover:text-red-500 rounded-xl transition-all duration-300"
            title="Voir les détails"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2
                2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1
                1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6"
              />
            </svg>
          </button>
        </div>
      </div>
      {
        isModalOpen &&
          <DeleteModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={deleteAdmin}
            title={`Supprimer l'${userType}`}
            message={`Êtes-vous sûr de vouloir supprimer l'${userType}`}
            itemName={admin.info.name}
          />
      }
    </div>
  );
}